import { api } from '../../api'
import type { Seller } from '../../../types/seller.types'
import type {
  Brand,
  BrandsFilters,
  BrandsResponse,
  CreateBrandPayload,
  CreateMemberPayload,
  Debtor,
  DynamicsIdCheckResponse,
  EditMemberPayload,
  MemberDetail,
  MembersFilters,
  MembersResponse,
  UpdateBrandDetailsPayload,
  UpdateBrandPayload,
} from '../../../types/organization.types'

// deprecate this (but still in use!!)
export const getOrganizationsTable = async ({ option }: { option: number }): Promise<Record<string, unknown>[]> => {
  const type = option == 0 ? 'members' : 'platforms'
  const res = await api.get<Record<string, unknown>[]>(`organizations/${type}/table`)

  return res.data
}

export const getMembers = async ({
  invoicing_active,
  page,
  ordering,
  search,
  page_size,
  fields,
}: MembersFilters): Promise<MembersResponse> => {
  const res = await api.get<MembersResponse>('members/', {
    params: { page, search, page_size, invoicing_active, ordering, fields },
  })
  return res.data
}

export const createMember = async (data: CreateMemberPayload): Promise<MemberDetail> => {
  const res = await api.post<MemberDetail>('members/', data)
  return res.data
}

export const editMember = async ({ data, id }: EditMemberPayload): Promise<MemberDetail> => {
  const res = await api.patch<MemberDetail>(`members/${id}/`, data)
  return res.data
}

export const deleteMember = async (id: string): Promise<void> => {
  await api.delete(`members/${id}/`)
}

export const updateMemberLogo = async (data: { id: string; logo: File | null }): Promise<MemberDetail> => {
  const formData = new FormData()
  formData.append('logo', data.logo ?? '')
  const res = await api.put<MemberDetail>(`members/${data.id}/logo/`, formData)
  return res.data
}

export const getMemberExtraData = async (data: { id: string; type: string }): Promise<Record<string, unknown>[]> => {
  const res = await api.get<Record<string, unknown>[]>(`members/${data.id}/${data.type}/`)
  return res.data
}

export const createMemberExtraData = async (data: {
  id: string
  type: string
  data: Record<string, string | File | string[]>
}): Promise<Record<string, unknown>> => {
  const formData = new FormData()
  Object.entries(data.data).forEach(([key, value]) => {
    if (value) {
      if (value) {
        if (Array.isArray(value)) {
          for (const [index, v] of value.entries()) {
            formData.append(`${key}[${index}]`, v as string)
          }
        } else {
          formData.append(key, value as string | File)
        }
      }
    }
  })
  const res = await api.post<Record<string, unknown>>(`members/${data.id}/${data.type}/`, formData)
  return res.data
}

export const deleteMemberExtraData = async (data: { id: string; type: string; data_id: string }): Promise<void> => {
  await api.delete(`members/${data.id}/${data.type}/${data.data_id}`)
}

export const deleteMemberContact = async (data: { member_id: string; id: string }): Promise<void> => {
  await api.delete(`members/${data.member_id}/contacts/${data.id}/`)
}

export const checkMemberDynamicsId = async ({
  member_id,
  dynamics_id,
}: {
  member_id?: string
  dynamics_id: string
}): Promise<DynamicsIdCheckResponse> => {
  const res = await api.get<DynamicsIdCheckResponse>(`members/dynamics_id/`, {
    params: { dynamics_id, member_id },
  })
  return res.data
}

export const checkDynamicsId = async ({
  member_id,
  dynamics_id,
  type,
}: {
  member_id?: string
  dynamics_id: string
  type: 'members' | 'brands' | 'debtors'
}): Promise<DynamicsIdCheckResponse> => {
  const res = await api.get<DynamicsIdCheckResponse>(`${type}/dynamics_id/`, {
    params: { dynamics_id, member_id },
  })
  return res.data
}

export const getBrands = async ({
  page,
  search,
  page_size,
  member,
  fields,
}: BrandsFilters): Promise<BrandsResponse> => {
  const res = await api.get<BrandsResponse>('members/brands/', {
    params: { page, search, page_size, member, ordering: 'name', fields },
  })
  return res.data
}

export const getBrandDetails = async (id: string): Promise<Brand> => {
  const res = await api.get<Brand>(`members/brands/${id}/`)
  return res.data
}

export const updateBrandDetails = async (data: UpdateBrandDetailsPayload): Promise<Brand> => {
  const res = await api.patch<Brand>(`members/brands/${data.id}/`, {
    whitelisted_seller_ids: data.whitelisted_seller_ids,
    whitelisted_domains: data.whitelisted_domains,
  })
  return res.data
}

export const deleteBrand = async (id: string): Promise<void> => {
  await api.delete(`members/brands/${id}/`)
}

export const getMemberDetails = async (id: string): Promise<MemberDetail> => {
  const res = await api.get<MemberDetail>(`members/${id}/`)
  return res.data
}

export const createBrand = async (data: CreateBrandPayload): Promise<Brand> => {
  const res = await api.post<Brand>('members/brands/', data)
  return res.data
}

export const updateBrand = async (data: UpdateBrandPayload): Promise<Brand> => {
  const res = await api.patch<Brand>(`members/brands/${data.id}/`, data)
  return res.data
}

export const uploadBrandLogo = async (data: { id: string; logo: File | null }): Promise<Brand> => {
  const formData = new FormData()
  formData.append('logo', data.logo ?? '')
  const res = await api.put<Brand>(`members/brands/${data.id}/logo/`, formData)
  return res.data
}

export const getBrandExtraData = async (data: {
  id: string
  type: string
  page_size?: number
  search?: string
}): Promise<Record<string, unknown>[]> => {
  const res = await api.get<Record<string, unknown>[]>(`members/brands/${data.id}/${data.type}/`, {
    params: { page_size: data.page_size ?? 100, search: data.search ?? '' },
  })
  return res.data
}

export const getBrandIprs = async (data: {
  brandId: string
  search?: string
  page_size?: number
}): Promise<Record<string, unknown>> => {
  const res = await api.get<Record<string, unknown>>(`members/brands/${data.brandId}/iprs/`, {
    params: {
      search: data.search ?? '',
      page_size: data.page_size ?? 15,
    },
  })
  return res.data
}

export const getBrandIprById = async (id: string): Promise<Record<string, unknown>> => {
  const res = await api.get<Record<string, unknown>>(`members/iprs/${id}`)
  return res.data
}

export const createBrandExtraData = async (data: {
  id: string
  type: string
  data: Record<string, string | File | string[]>
}): Promise<Record<string, unknown>> => {
  const formData = new FormData()
  Object.entries(data.data).forEach(([key, value]) => {
    if (value) {
      if (Array.isArray(value)) {
        for (const [index, v] of value.entries()) {
          formData.append(`${key}[${index}]`, v as string)
        }
      } else {
        formData.append(key, value as string | File)
      }
    }
  })
  const res = await api.post<Record<string, unknown>>(`members/brands/${data.id}/${data.type}/`, formData)
  return res.data
}

export const deleteBrandExtraData = async (data: { id: string; type: string; data_id: string }): Promise<void> => {
  await api.delete(`members/brands/${data.id}/${data.type}/${data.data_id}`)
}

export const checkBrandDynamicsId = async ({
  brand_id,
  dynamics_id,
}: {
  brand_id?: string
  dynamics_id: string
}): Promise<DynamicsIdCheckResponse> => {
  const res = await api.get<DynamicsIdCheckResponse>(`members/brands/dynamics_id/`, {
    params: { dynamics_id, brand_id },
  })
  return res.data
}

export const getDebtors = async (data: { member_id: string }): Promise<Debtor[]> => {
  const res = await api.get<Debtor[]>(`members/${data.member_id}/debtors/`)
  return res.data
}

export const createDebtor = async (data: {
  member_id: string
  name: string
  dynamics_id: string
}): Promise<Debtor> => {
  const res = await api.post<Debtor>(`members/${data.member_id}/debtors/`, data)
  return res.data
}

export const deleteDebtor = async (data: { member_id: string; id: string }): Promise<void> => {
  await api.delete(`members/${data.member_id}/debtors/${data.id}/`)
}

export const getWhitelistedSellers = async (id: string): Promise<{ sellers: Seller[] }> => {
  const res = await api.get<{ sellers: Seller[] }>(`members/brands/${id}/whitelisted/sellers/`)
  return res.data
}

export const getWhitelistedDomains = async (
  id: string,
): Promise<{ brand_id: string; domains: string[]; count: number }> => {
  const res = await api.get<{ brand_id: string; domains: string[]; count: number }>(
    `members/brands/${id}/whitelisted/domains/`,
  )
  return res.data
}

export const addWhitelistedSeller = async (brandId: string, sellerId: string): Promise<void> => {
  await api.post(`members/brands/${brandId}/whitelisted/sellers/`, { seller_id: sellerId })
}

export const removeWhitelistedSeller = async (brandId: string, sellerId: string): Promise<void> => {
  await api.delete(`members/brands/${brandId}/whitelisted/sellers/${sellerId}/`)
}

export const addWhitelistedDomain = async (brandId: string, domain: string): Promise<void> => {
  await api.post(`members/brands/${brandId}/whitelisted/domains/`, { domain })
}

// ── Sectors ──────────────────────────────────────

export const getSectors = async (params: {
  search?: string
  page?: number
  page_size?: number
}): Promise<{ data: { id: string; name: string }[] }> => {
  const res = await api.get<{ data: { id: string; name: string }[] }>('sectors/', { params })
  return res.data
}

export const createSector = async (data: { name: string }): Promise<{ id: string; name: string }> => {
  const res = await api.post<{ id: string; name: string }>('sectors/', data)
  return res.data
}
